import { currentAccountStorageScope, readAccountScopedStorage, writeAccountScopedStorage, removeAccountScopedStorage, removeAccountScopedStorageForScope } from "@/shared/storage/account-scope";
import { captureRetainedAudioOwner, retainedAudioOwnerIsCurrent, type RetainedAudioOwner, type RetainedAudioRecording } from "./retained-audio-store";
import { retainedAudioCheckpointIsEligible, retainedAudioCheckpointMatchesRecording, RETAINED_AUDIO_RESUME_MAX_AGE_MS } from "./retained-audio-resume-policy.mjs";
export type RetainedAudioCheckpoint = {
  version: 1; ownerScope: string; recordingId: string; kind: RetainedAudioRecording["kind"];
  savedAtEpochMs: number; expiresAtEpochMs: number; recordingExpiresAtEpochMs: number; positionMs: number; durationMs: number;
};
const KEY = "tb_retained_audio_resume_v1";
const changes = new EventTarget();
let disposeActive: (() => void) | null = null;
export function onRetainedAudioResumeChanged(listener: () => void) {
  changes.addEventListener("change", listener);
  return () => changes.removeEventListener("change", listener);
}
function changed() { changes.dispatchEvent(new Event("change")); }
/** Explicit scope also supports purging a deleted account after the session moves. */
export function clearRetainedAudioCheckpointForScope(scope: string, recordingId?: string) {
  try {
    if (scope === currentAccountStorageScope()) {
      if (recordingId) {
        const raw = readAccountScopedStorage(KEY);
        if (!raw || JSON.parse(raw).recordingId !== recordingId) return;
      }
      removeAccountScopedStorage(KEY);
    } else if (!recordingId) removeAccountScopedStorageForScope(KEY, scope);
  } catch { /* Disabled browser storage cannot create a resume offer. */ }
  changed();
}
export function readRetainedAudioCheckpoint(owner = captureRetainedAudioOwner()): RetainedAudioCheckpoint | null {
  if (!retainedAudioOwnerIsCurrent(owner)) return null;
  try {
    const raw = readAccountScopedStorage(KEY);
    if (!raw) return null;
    if (raw.length > 2048) throw new Error("audio_resume_invalid");
    const value: unknown = JSON.parse(raw);
    if (retainedAudioCheckpointIsEligible(value, owner.scope)) return value as RetainedAudioCheckpoint;
  } catch { /* Corrupt or disabled storage is unavailable, never reconstruction. */ }
  clearRetainedAudioCheckpointForScope(owner.scope);
  return null;
}
export function retainedAudioResumeForRows(rows: RetainedAudioRecording[], owner = captureRetainedAudioOwner()): RetainedAudioCheckpoint | null {
  const checkpoint = readRetainedAudioCheckpoint(owner);
  if (!checkpoint) return null;
  const recording = rows.find((row) => row.id === checkpoint.recordingId);
  if (retainedAudioCheckpointMatchesRecording(checkpoint, recording, owner.scope)) return checkpoint;
  clearRetainedAudioCheckpointForScope(owner.scope);
  return null;
}
/** Every new player invalidates an earlier pause offer and its event callbacks. */
export function invalidateRetainedAudioPauseCheckpoint() {
  disposeActive?.(); disposeActive = null;
  clearRetainedAudioCheckpointForScope(captureRetainedAudioOwner().scope);
}
/** Attach only after exact accepted bytes have loaded and retention committed. */
export function bindRetainedAudioPauseCheckpoint(input: {
  owner: RetainedAudioOwner; recording: RetainedAudioRecording; player: HTMLAudioElement; isPlayerCurrent: () => boolean;
}) {
  disposeActive?.();
  const { owner, recording, player } = input;
  let disposed = false;
  const current = () => !disposed && input.isPlayerCurrent() && retainedAudioOwnerIsCurrent(owner) && recording.ownerScope === owner.scope;
  const pause = () => {
    const durationMs = Math.round(player.duration * 1000);
    const positionMs = Math.round(player.currentTime * 1000);
    if (!current() || player.ended || !Number.isSafeInteger(durationMs) || Math.abs(durationMs - recording.durationMs) > 1000 ||
      !Number.isSafeInteger(positionMs) || positionMs <= 0 || positionMs >= durationMs) return;
    const savedAtEpochMs = Date.now();
    const checkpoint: RetainedAudioCheckpoint = { version: 1, ownerScope: owner.scope, recordingId: recording.id, kind: recording.kind,
      savedAtEpochMs, expiresAtEpochMs: Math.min(savedAtEpochMs + RETAINED_AUDIO_RESUME_MAX_AGE_MS, recording.expiresAtEpochMs),
      recordingExpiresAtEpochMs: recording.expiresAtEpochMs, positionMs, durationMs };
    if (!retainedAudioCheckpointMatchesRecording(checkpoint, recording, owner.scope)) return;
    try { writeAccountScopedStorage(KEY, JSON.stringify(checkpoint)); changed(); } catch { /* Playback stays available without persistence. */ }
  };
  const clear = () => { if (current()) clearRetainedAudioCheckpointForScope(owner.scope, recording.id); };
  player.addEventListener("pause", pause); player.addEventListener("ended", clear); player.addEventListener("play", clear);
  disposeActive = () => { disposed = true; player.removeEventListener("pause", pause); player.removeEventListener("ended", clear); player.removeEventListener("play", clear); };
  if (player.paused && !player.ended) pause();
}
